import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, OnDestroy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';

import { FormGroup } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { debounceTime, distinctUntilChanged, ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-table';
import { DamageCodeService } from '../../../services/damage-code.service';
import { ToastrService } from '../../../services/toastr-service';
import { DamageCodeHeader } from '../../../models/damage-code-header.model';
import { DamageCodeFilterForm } from '../../../form-model/damage-code-filter.form.model';
import { DamageCodeFilter } from '../../../models/damage-code-filter.model';
import { DamageCodeSearchTextForm } from '../../../form-model/damage-code-search-text.form.model';
import { DamageCodeListGridColumns } from './damage-code-grid.config';
import { AuthPolicies } from '../../../auth/auth-policies';

@Component({
  selector: 'app-damage-code-grid',
  imports: [RouterModule, NgxSkeletonLoaderModule, LoaderComponent],
  templateUrl: './damage-code-grid.component.html',
  styleUrl: './damage-code-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DamageCodeGridComponent implements OnInit, OnDestroy {
  data: DamageCodeHeader[] = [];
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 30 }, (_, i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  filterParam: DamageCodeFilter = {};
  getDamageCodeApiCallStatus: APICallStatus = 'NOT_STARTED';
  hasData: boolean = false;
  urlNavigate: string | undefined;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    damageCodeFilterForm: FormGroup<DamageCodeFilterForm>;
    damageCodeSearchTextForm: FormGroup<DamageCodeSearchTextForm>;
  }>;
  columns = DamageCodeListGridColumns;
  openDeleteConfirmation: boolean = false;
  selectedRow: DamageCodeHeader | null = null;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() isDamageCodesList: boolean = true;
  constructor(
    private readonly damageCodeService: DamageCodeService,
    private readonly router: Router,
    private readonly toastrService: ToastrService,
    public authPolicy: AuthPolicies,
  ) {}

  ngOnInit() {
    this.urlNavigate = '/master-data/damage-code';
    this.getDamageCode();
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      if (
        this.filterFormGroup.controls.damageCodeFilterForm.controls.applyDCFilter.value ||
        this.filterFormGroup.controls.damageCodeSearchTextForm.controls.appliedDCSearchTextFilter.value
      ) {
        this.onApplyFilter();
      }
    });
  }

  onSortDirectionChange(event: Event) {
    let customEvent = event as CustomEvent;
    let sortColumn: string = this.sortColumnName(customEvent.detail.column);
    let sortDirection: string = customEvent.detail.direction == 'ascending' ? 'asc' : 'desc';
    if (sortColumn != 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getDamageCode();
    }
  }

  private sortColumnName(gridColumnName: string): string {
    return gridColumnName;
  }

  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getDamageCode();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getDamageCode(true);
    }
  }

  onEdit(row: DamageCodeHeader) {
    const base = this.urlNavigate ?? '/master-data/damage-code';
    // Extract numeric prefix if damageType is formatted like "0 - Structural"
    const raw = row.damageType;
    let dtParam: number | undefined;
    if (raw === null || raw === undefined || raw === '') {
      dtParam = undefined;
    } else {
      const m = /(\d+)/.exec(String(raw));
      dtParam = m ? Number(m[0]) : undefined;
    }

    const navArray = dtParam === undefined ? [base, row.damageCode, 'edit'] : [base, row.damageCode, dtParam, 'edit'];

    this.router.navigate(navArray).catch(() => {
      const url =
        dtParam === undefined
          ? `${base}/${encodeURIComponent(String(row.damageCode))}/edit`
          : `${base}/${encodeURIComponent(String(row.damageCode))}/${encodeURIComponent(String(dtParam))}/edit`;
      this.router.navigateByUrl(url).catch(() => {});
    });
  }

  onDelete(row: DamageCodeHeader) {
    this.selectedRow = row;
    this.openDeleteConfirmation = true;
  }

  cancelDelete(): void {
    this.openDeleteConfirmation = false;
    // navigate back to list to ensure the user lands on damage-code page
    if (this.urlNavigate) {
      this.router.navigate([this.urlNavigate]).catch(() => {});
    }
    this.selectedRow = null;
  }

  confirmDelete(): void {
    if (!this.selectedRow) {
      this.openDeleteConfirmation = false;
      return;
    }
    const row = this.selectedRow;
    // extract numeric damageType if present
    const raw = row.damageType;
    const m = /(\d+)/.exec(String(raw));
    const dtParam = m ? Number(m[0]) : undefined;
    if (dtParam === undefined) {
      // if damageType is missing, conservatively close modal and do nothing
      this.openDeleteConfirmation = false;
      this.selectedRow = null;
      return;
    }
    this.damageCodeService
      .deleteDamageCode(row.damageCode, dtParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.openDeleteConfirmation = false;
          this.selectedRow = null;
          // refresh list
          this.getDamageCode();
          // show success toast
          this.toastrService.showToastr(`Damage code '${row.damageCode}' deleted successfully`, 'success');
        },
        error: () => {
          this.openDeleteConfirmation = false;
          this.selectedRow = null;
          this.toastrService.showToastr(`Failed to delete damage code '${row.damageCode}'`, 'error');
        },
      });
  }

  async onApplyFilter() {
    if (this.filterFormGroup.controls.damageCodeFilterForm.valid) {
      const filters: DamageCodeFilter = {};

      if (this.filterFormGroup.controls.damageCodeFilterForm.controls.damageCodeDescriptionList.value) {
        const rawVals: string[] = this.filterFormGroup.controls.damageCodeFilterForm.controls.damageCodeDescriptionList.value;
        // map selected ids back to full "CODE - Description" strings using the unique list from service
        const list = await firstValueFrom(this.damageCodeService.getUniqueDamageCodes());
        const mapped = rawVals.map((v) => {
          const parts = (v || '').toString().split('__');
          const idx = parts.length > 1 ? Number.parseInt(parts[1], 10) : -1;
          const code = parts[0];
          let found = undefined as (typeof list)[0] | undefined;
          if (!Number.isNaN(idx) && idx >= 0 && idx < (list || []).length) {
            found = list[idx];
          }
          found ??= (list || []).find((x) => x.code === code);
          return found ? `${found.code} - ${found.description}` : code;
        });
        // dedupe
        filters.damageCodeDescriptionList = Array.from(new Set(mapped)).filter(Boolean);
      }

      if (this.filterFormGroup.controls.damageCodeFilterForm.controls.damageTypesList.value) {
        filters.damageTypesList = this.filterFormGroup.controls.damageCodeFilterForm.controls.damageTypesList.value;
      }

      if (this.filterFormGroup.controls.damageCodeSearchTextForm.controls.searchText.value) {
        filters.searchText = this.filterFormGroup.controls.damageCodeSearchTextForm.controls.searchText.value;
      }
      this.filterParam = filters;
      this.currentPage = 1;
      this.getDamageCode();
    }
  }

  getDamageCode(isPageChange: boolean = false): void {
    this.data = [];
    this.getDamageCodeApiCallStatus = 'IN_PROGRESS';
    this.damageCodeService
      .getDamageCode(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          const body = response.body || [];
          this.data = body.map((item) => ({
            ...item,
            id: `${item.damageCode}__${item.damageType}`,
          }));
          this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
          this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
          this.currentPage = +headers.get('Current-Page')!;
          this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
          this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
          this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          this.apiVersion = headers.get('API-Version') ?? undefined;
          this.getDamageCodeApiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
        error: (err) => {
          this.getDamageCodeApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }

  clearFilter() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.damageCodeSearchTextForm.controls.appliedDCSearchTextFilter.setValue(true);
    this.filterFormGroup.controls.damageCodeFilterForm.controls.applyDCFilter.setValue(true);
  }
}
